import { Component, createEffect, on, onCleanup, onMount } from "solid-js"
import { unlockAmbient } from "../lib/ambient-audio"
import {
    setClearAgentMarkersCallback,
    setClearPopupsCallback,
    setClockAnnotationCallback,
    setCollisionDetectionCallback,
    setEndTimeReachedCallback,
    setPositionUpdateCallback,
    setResetViewCallback,
    type AgentPosition,
} from "../lib/animation"
import {
    clearAgentWaypoints,
    clearAllAgentMarkers,
    clearAllWaypoints,
    clearViewportPinMarkers,
    createLeafletMap,
    detectAndHideOverlappingLabels,
    drawAgentWaypoints,
    drawViewportPinMarkers,
    setDarkMode,
    updateAgentIconsForZoom,
    updateAgentPositions,
    type LeafletMap,
} from "../lib/leaflet-wrapper"
import { unlockTimelineAudio } from "../lib/timeline-audio"
import { getActiveViewportPin, isDefaultStartPin } from "../lib/viewport-utils"
import { useAppStore } from "../stores/app"
import { clock } from "../stores/clock"
import { mapData } from "../stores/map"
import { useScenarioStore } from "../stores/scenario"
import type { AppMode, ClockAnnotation } from "../types"
import { formatSimTime } from "../types"

interface MapViewProps {
    onMapClick: (latlng: [number, number], shiftKey?: boolean) => void
    onVisibleAgentsChange: (ids: Set<string>) => void
    onClockAnnotation?: (annotation: ClockAnnotation) => void
    onAgentClick?: (agentId: string) => void
    onEndTimeReached?: () => void
}

const MapView: Component<MapViewProps> = (props) => {
    const [scenario, scenarioActions] = useScenarioStore()
    const [app, appActions] = useAppStore()
    let containerRef!: HTMLDivElement
    let lmap: LeafletMap | null = null
    let popupContainer!: HTMLDivElement
    // Keep a mutable ref so the callback (set once in onMount) always reads the latest value
    let showLabelsRef = true
    let modeRef: AppMode = "edit"
    let editTabRef: string | undefined = undefined
    let selectedViewportPinIdRef: string | null | undefined = undefined
    let audioUnlocked = false
    let isProgrammaticPan = false // Track if we're doing a programmatic flyTo

    // Unlock audio on any map interaction
    const unlockAudio = () => {
        if (!audioUnlocked) {
            audioUnlocked = true
            unlockAmbient()
            unlockTimelineAudio()
        }
    }

    createEffect(() => {
        showLabelsRef = app.showLabels
    })
    createEffect(() => {
        modeRef = app.mode
    })
    createEffect(() => {
        editTabRef = app.editTab
    })
    createEffect(() => {
        selectedViewportPinIdRef = app.selectedViewportPinId
    })

    // Auto-pan to viewport pins during simulation
    let lastAppliedPinId: string | null = null

    // When a new scenario is loaded (loadCount increments), immediately fly to the new
    // scenario's default-start pin. At this point the batch has already committed the
    // new viewportPins into the store, so we always read the correct position here.
    // This avoids the race where the auto-pan effect fires from setClock (before
    // loadScenario) with the old pins, setting lastAppliedPinId to "viewport-first",
    // and then correctly-updated viewportPins cause a re-run that gets skipped because
    // the mutable lastAppliedPinId still matches "viewport-first".
    createEffect(on(() => scenario.loadCount, () => {
        lastAppliedPinId = null
        if (!lmap) return
        const startPin =
            scenario.viewportPins.find((p) => isDefaultStartPin(p)) ?? scenario.viewportPins[0]
        if (!startPin) return
        if (startPin.position[0] === 0 && startPin.position[1] === 0) return
        isProgrammaticPan = true
        lmap.map.flyTo(startPin.position, startPin.zoom, { duration: 1.0 })
    }, { defer: true }))

    // Reset lastAppliedPinId when clock is reset to start time (not on pause)
    createEffect(() => {
        const isAtStartTime =
            clock.currentTime.hour === scenario.meta.startTime.hour &&
            clock.currentTime.minute === scenario.meta.startTime.minute &&
            clock.currentTime.second === scenario.meta.startTime.second

        // Only clear when at start time AND not running (reset state)
        if (isAtStartTime && !clock.isRunning) {
            lastAppliedPinId = null
        }
    })

    // Auto-pan: fly to active viewport pin during simulation OR when clock is at start (reset)
    createEffect(() => {
        if (!lmap) return

        // Check if clock is at start time (reset scenario)
        const isAtStartTime =
            clock.currentTime.hour === scenario.meta.startTime.hour &&
            clock.currentTime.minute === scenario.meta.startTime.minute &&
            clock.currentTime.second === scenario.meta.startTime.second

        // Always fly to default-start pin when at start time (handles reset)
        // Otherwise, only fly during simulation if autoPan is enabled
        if (!isAtStartTime && (!scenario.autoPan || app.mode !== "simulate")) {
            return
        }

        const activePin = getActiveViewportPin(scenario.viewportPins, clock.currentTime, scenario.meta.startTime)
        if (!activePin) return

        // Only pan if it's a different pin than last time
        if (activePin.id === lastAppliedPinId) {
            return
        }

        // Check if pin has valid position
        if (activePin.position[0] === 0 && activePin.position[1] === 0) {
            return
        }
        lastAppliedPinId = activePin.id
        isProgrammaticPan = true
        const duration = isAtStartTime ? 1.0 : 1.5
        lmap.map.flyTo(activePin.position, activePin.zoom, { duration })
    })

    // Reactive update: when default-start viewport pin position changes while at start time
    createEffect(() => {
        if (!lmap) return

        const firstPin = scenario.viewportPins[0]
        if (!firstPin || firstPin.time !== "default-start") return

        // Only update if clock is at start time
        const isAtStartTime =
            clock.currentTime.hour === scenario.meta.startTime.hour &&
            clock.currentTime.minute === scenario.meta.startTime.minute &&
            clock.currentTime.second === scenario.meta.startTime.second
        if (!isAtStartTime) return

        // Check if position is valid
        if (firstPin.position[0] === 0 && firstPin.position[1] === 0) return

        // Update map position and zoom to match edited pin
        isProgrammaticPan = true
        lmap.map.setView(firstPin.position, firstPin.zoom)
    })

    // Fly to viewport pin when selected in edit mode
    createEffect(() => {
        if (!lmap || app.mode !== "edit" || app.editTab !== "viewport") return

        const selectedId = app.selectedViewportPinId
        if (!selectedId) return

        const selectedPin = scenario.viewportPins.find((p) => p.id === selectedId)
        if (!selectedPin) return

        // Check if position is valid
        if (selectedPin.position[0] === 0 && selectedPin.position[1] === 0) return

        // Fly to the selected pin
        isProgrammaticPan = true
        lmap.map.flyTo(selectedPin.position, selectedPin.zoom, { duration: 0.8 })
    })

    // Draw viewport pin markers in edit mode when viewport tab is active
    createEffect(
        on(
            () => [
                app.mode,
                app.editTab,
                app.selectedViewportPinId,
                scenario.viewportPins.length,
                ...scenario.viewportPins.map((p) => `${p.position[0]},${p.position[1]}`),
            ],
            () => {
                if (!lmap) return

                if (app.mode === "edit" && app.editTab === "viewport") {
                    drawViewportPinMarkers(lmap, scenario.viewportPins, app.selectedViewportPinId || null)
                } else {
                    clearViewportPinMarkers(lmap)
                }
            },
            { defer: true }
        )
    )

    // Auto-pan to selected pin when selected in edit mode
    createEffect(
        on(
            () => app.selectedViewportPinId,
            (pinId) => {
                if (!lmap || app.mode !== "edit" || app.editTab !== "viewport") return
                if (!pinId) return

                const pin = scenario.viewportPins.find((p) => p.id === pinId)
                if (!pin) return

                // Only pan if pin has valid position
                if (pin.position[0] === 0 && pin.position[1] === 0) return

                // Pan to the pin with smooth animation
                isProgrammaticPan = true
                lmap.map.flyTo(pin.position, lmap.map.getZoom(), { duration: 0.5 })
            }
        )
    )

    onMount(() => {
        // viewportPins may not be loaded yet during initial mount
        const firstPin = scenario.viewportPins[0]
        const initialCenter: [number, number] = firstPin?.position || [59.33914127606005, 18.059999942779545]
        const initialZoom = firstPin?.zoom || 18

        lmap = createLeafletMap(
            containerRef,
            mapData,
            (latlng, shiftKey) => {
                unlockAudio() // Unlock on map click
                props.onMapClick(latlng, shiftKey)
            },
            scenario.meta.darkMode || false,
            initialCenter,
            initialZoom
        )

        // Reactive effect: Update map view when viewport pins are loaded after mount
        let hasSetInitialView = !!firstPin
        createEffect(() => {
            if (!lmap || hasSetInitialView) return
            const firstPin = scenario.viewportPins[0]
            if (firstPin && firstPin.position[0] !== 0 && firstPin.position[1] !== 0) {
                lmap.map.setView(firstPin.position, firstPin.zoom)
                hasSetInitialView = true
            }
        })

        // Unlock audio on any map interaction
        lmap.map.on("click", unlockAudio)
        lmap.map.on("drag", unlockAudio)
        lmap.map.on("zoom", unlockAudio)
        lmap.map.on("touchstart", unlockAudio)

        // Handle waypoint dragging
        if (lmap) {
            lmap.onWaypointDragEnd = (agentId, segIdx, wpIdx, newPosition) => {
                scenarioActions.updateWaypointPosition(agentId, segIdx, wpIdx, newPosition)
                scenarioActions.scheduleSave()
            }

            // Handle viewport pin dragging (Shift+drag)
            lmap.onViewportPinDragEnd = (pinId, newPosition) => {
                scenarioActions.updateViewportPin(pinId, { position: newPosition })
                scenarioActions.scheduleSave()
            }

            // Handle viewport pin click selection
            lmap.onViewportPinClick = (pinId) => {
                appActions.selectViewportPin(pinId)
            }

            // Handle agent clicks in simulate mode
            lmap.onAgentClick = (agentId, _event) => {
                if (modeRef === "simulate" && props.onAgentClick) {
                    props.onAgentClick(agentId)
                }
            }
        }

        // Handle zoom changes to update icon sizes
        lmap.map.on("zoomend", () => {
            if (!lmap) return
            const lookup = new Map(scenario.agents.map((a) => [a.id, a]))
            updateAgentIconsForZoom(lmap, lookup)
            // Check for label collisions after zoom - immediate to avoid visual jump
            detectAndHideOverlappingLabels(lmap, lookup, clock.speedMultiplier, true)

            // NOTE: Zoom changes are NOT captured automatically
            // Only the dropdown in ViewportPanel updates zoom to prevent erratic behavior
        })

        // Handle map pan - only track programmatic pans
        lmap.map.on("moveend", () => {
            if (!lmap) return

            // Skip if this was a programmatic pan (flyTo)
            if (isProgrammaticPan) {
                isProgrammaticPan = false
                return
            }

            // NOTE: Position changes are NOT captured automatically
            // Only Shift+drag (onViewportPinDragEnd) updates position to prevent erratic behavior
        })

        setPositionUpdateCallback((positions: AgentPosition[]) => {
            if (!lmap) return
            const lookup = new Map(scenario.agents.map((a) => [a.id, a]))
            updateAgentPositions(lmap, positions, lookup, showLabelsRef)
            const visible = new Set<string>()
            for (const p of positions) {
                if (p.visible) visible.add(p.agentId)
            }
            props.onVisibleAgentsChange(visible)
        })

        setCollisionDetectionCallback(() => {
            if (!lmap) return
            const lookup = new Map(scenario.agents.map((a) => [a.id, a]))
            detectAndHideOverlappingLabels(lmap, lookup, clock.speedMultiplier)
        })

        setClockAnnotationCallback((annotation: ClockAnnotation) => {
            // Show desktop popups in simulate mode
            if (modeRef === "simulate" && !app.isMobile) {
                showClockPopup(annotation)
            }
            // Also call the parent callback (for mobile banner)
            props.onClockAnnotation?.(annotation)
        })

        setClearPopupsCallback(() => {
            clearAllPopups()
        })

        setClearAgentMarkersCallback(() => {
            if (lmap) {
                clearAllAgentMarkers(lmap)
                clearAllWaypoints(lmap)
            }
        })

        setEndTimeReachedCallback(() => {
            props.onEndTimeReached?.()
        })

        setResetViewCallback(() => {
            lastAppliedPinId = null
            if (!lmap) return
            const startPin =
                scenario.viewportPins.find((p) => isDefaultStartPin(p)) ??
                scenario.viewportPins[0]
            if (!startPin) return
            if (startPin.position[0] === 0 && startPin.position[1] === 0) return
            isProgrammaticPan = true
            lmap.map.flyTo(startPin.position, startPin.zoom, { duration: 0.8 })
        })

        // Draw viewport pins if we're starting in edit mode with viewport tab active
        if (app.mode === "edit" && app.editTab === "viewport") {
            drawViewportPinMarkers(lmap, scenario.viewportPins, app.selectedViewportPinId || null)
        }
    })

    // React to dark mode changes
    createEffect(() => {
        if (lmap) {
            const isDark = scenario.meta.darkMode || false
            setDarkMode(lmap, mapData, isDark)
        }
    })

    onCleanup(() => {
        if (lmap) {
            lmap.destroy()
            lmap = null
        }
    })

    function showClockPopup(annotation: ClockAnnotation) {
        const el = document.createElement("div")
        el.className = "clock-popup"
        el.innerHTML = `<strong>${formatSimTime(annotation.time)}</strong><br/>${annotation.text.replace(/\n/g, "<br/>")}`

        // Click to unfold on desktop
        el.addEventListener("click", () => {
            el.classList.toggle("unfolded")
        })

        popupContainer.appendChild(el)
        setTimeout(() => {
            if (el.parentNode) el.parentNode.removeChild(el)
        }, 8000)
    }

    function clearAllPopups() {
        while (popupContainer.firstChild) {
            popupContainer.removeChild(popupContainer.firstChild)
        }
    }

    // In edit mode, only draw the selected agent's path
    createEffect(
        on(
            () =>
                [
                    scenario.agents.map((a) => ({
                        ...a,
                        segments: a.segments.map((s) => ({ ...s, waypoints: s.waypoints.map((w) => ({ ...w })) })),
                    })),
                    app.selectedAgentId,
                    app.mode,
                    app.showLabels,
                ] as const,
            () => {
                if (!lmap) return

                if (app.mode === "simulate") {
                    for (const agent of scenario.agents) clearAgentWaypoints(lmap, agent.id)
                    return
                }

                const currentIds = new Set(scenario.agents.map((a) => a.id))
                for (const [id] of lmap.waypointLayers) {
                    if (!currentIds.has(id)) clearAgentWaypoints(lmap, id)
                }

                for (const agent of scenario.agents) {
                    const isSelected = agent.id === app.selectedAgentId
                    if (isSelected) {
                        drawAgentWaypoints(lmap, agent, true, app.showLabels)
                    } else {
                        clearAgentWaypoints(lmap, agent.id)
                    }
                }
            }
        )
    )

    // When toggling labels, update existing markers immediately
    createEffect(
        on(
            () => app.showLabels,
            () => {
                if (!lmap) return
                for (const [_id, marker] of lmap.agentMarkers) {
                    const el = marker.getElement()
                    if (!el) continue
                    el.classList.toggle("label-hidden", !app.showLabels)
                    if (app.showLabels) {
                        // When re-enabling labels, clear inline opacity left by collision
                        // detection so CSS can cleanly control visibility again
                        const labelEl = el.querySelector(".sim-label") as HTMLElement
                        if (labelEl) {
                            labelEl.style.transition = ""
                            labelEl.style.opacity = ""
                            labelEl.style.pointerEvents = ""
                        }
                    }
                }
            }
        )
    )

    createEffect(
        on(
            () => app.mode,
            (mode) => {
                if (mode === "edit" && lmap) clearAllAgentMarkers(lmap)
                // Invalidate map size when mode changes (sidebar appears/disappears)
                if (lmap) {
                    setTimeout(() => {
                        lmap!.map.invalidateSize()
                    }, 100)
                }
            }
        )
    )

    return (
        <div class="relative h-full w-full">
            <div
                ref={containerRef}
                class="h-full w-full"
                classList={{
                    "cursor-crosshair":
                        app.mode === "edit" && app.editTab === "viewport" && !!app.selectedViewportPinId,
                }}
                style={{ "min-height": "400px" }}
            />
            <div ref={popupContainer!} class="clock-popup-container" />
        </div>
    )
}

export default MapView
